// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import api, { API_BASE } from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    const hydrateUser = async () => {
      const token = localStorage.getItem('token');
      try {
        if (!token) return;

        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 <= Date.now()) {
          localStorage.removeItem('token');
          return;
        }

        // Always fetch /me to get full user data (name, email, role)
        const res = await api.get('/me');
        setUser({ id: res.data.id, email: res.data.email, role: res.data.role, name: res.data.name });
      } catch (e) {
        console.error('Error al hidratar sesión:', e);
        localStorage.removeItem('token');
      } finally {
        setIsAuthLoading(false);
      }
    };

    hydrateUser();
  }, []);

  // LOGIN usando fetch para evitar cualquier transformación de Axios
  const login = async (email, password, captchaToken = null) => {
    const url = `${API_BASE}/auth/login`;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',      // JSON puro
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        username: email,
        password: password,
        captcha_token: captchaToken,            // nombre exacto que espera el backend
      }),
      credentials: 'include',                   // por si usas cookies/sesión
    });

    const data = await res.json().catch(() => ({}));

    const needsCaptchaHeader =
      res.headers.get('x-needs-captcha') === 'true' ||
      res.headers.get('X-Needs-Captcha') === 'true';
    const needsCaptchaBody =
      typeof data?.detail === 'string' && data.detail.toLowerCase().includes('captcha');
    const needsCaptcha = needsCaptchaHeader || needsCaptchaBody;

    const licensePendingHeader =
      res.headers.get('x-license-pending') === 'true' ||
      res.headers.get('X-License-Pending') === 'true';
    const licensePendingBody =
      typeof data?.detail === 'string' && data.detail.toLowerCase().includes('licencia pendiente');
    const licensePending = licensePendingHeader || licensePendingBody;

    if (!res.ok) {
      // Lanza un error con shape similar al de axios para que Login.js lo pueda formatear
      const error = new Error('Login failed');
      error.status = res.status;
      error.data = data;
      error.needsCaptcha = needsCaptcha;
      error.licensePending = licensePending;
      throw error;
    }

    const { access_token } = data;
    if (!access_token) {
      const error = new Error('Respuesta inválida del servidor');
      error.status = 500;
      error.data = data;
      throw error;
    }

    localStorage.setItem('token', access_token);
    const decoded = jwtDecode(access_token);

    if (decoded.role) {
      // Also fetch /me to get name
      try {
        const me = await api.get('/me');
        setUser({ id: me.data.id, email: me.data.email, role: me.data.role, name: me.data.name });
      } catch {
        setUser({ email: decoded.sub, role: decoded.role });
      }
    } else {
      const me = await api.get('/me');
      setUser({ id: me.data.id, email: me.data.email, role: me.data.role, name: me.data.name });
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const value = { user, login, logout, isAuthLoading, setUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => useContext(AuthContext);
